import { useState, useEffect } from 'react'
import { CONTAINER_ASPECT_RATIO } from '../../../utility/constants'

/**
 * useFixedContainerSize - Hook to get the largest box with the wrapper (parent) dimensions
 * which maintains the appropriate aspect ratio. Updates whenever the wrapper resizes.
 *
 * @param {?number} wrapperWidth - the width of the parent div
 * @param {?number} wrapperHeight - the height of the parent div
 *
 * @return {
 *	 width: ?number, - the largest box's width which fills the wrapper dimensions while still maintaining the aspect ratio
 *	 height: ?number, - the largest box's height which fills the wrapper dimensions while still maintaining the aspect ratio
 *}
 */
export function useFixedContainerSize(
	wrapperWidth: ?number,
	wrapperHeight: ?number
): { height: ?number, width: ?number } {
	const [divSize, setDivSize] = useState(() =>
		getAspectSize(wrapperWidth, wrapperHeight)
	)

	useEffect(() => {
		setDivSize(getAspectSize(wrapperWidth, wrapperHeight))
	}, [wrapperWidth, wrapperHeight])

	return divSize
}

/**
 * getAspectSize - Given the dimensions of the parent div get the dimensions of the largest box which can
 * fit inside the parent div while still maintaining the correct aspect ratio.
 *
 * @param {?number} wrapperWidth - the width of the parent div
 * @param {?number} wrapperHeight - the height of the parent div
 *
 * @return {
 *	 width: ?number, - the largest box's width which fills the wrapper dimensions while still maintaining the aspect ratio
 *	 height: ?number, - the largest box's height which fills the wrapper dimensions while still maintaining the aspect ratio
 *}
 */
export function getAspectSize(
	wrapperWidth: ?number,
	wrapperHeight: ?number
): { height: ?number, width: ?number } {
	if (!wrapperWidth || !wrapperHeight) {
		return { height: null, width: null }
	}
	return {
		height: Math.min(wrapperHeight, wrapperWidth / CONTAINER_ASPECT_RATIO),
		width: Math.min(wrapperWidth, wrapperHeight * CONTAINER_ASPECT_RATIO),
	}
}
